import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Modal,
  Switch,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { ClientInterface } from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import CustomTooltip from "../CustomTooltip";
import {
  formatCNPJ,
  formatCPF,
  formatPhone
} from "../../common/utils/formatters";
import CnpjInput from "../mask/CnpjInput";
import CpfInput from "../mask/CpfInput";
import PhoneInput from "../mask/PhoneInput";
import { InputSection, InputText } from "../style/ClientCreateModal.styled";

import { usePageToast } from "../../hooks/usePageToast";
import PageToaster from "../toaster/PageToaster";

interface ClientCreateModalProps {
  open: boolean;
  onClose: () => void;
  client?: ClientInterface | null;
}

const ClientCreateModal: React.FC<ClientCreateModalProps> = ({
  open,
  onClose,
  client
}) => {
  const [clientName, setClientName] = useState("");
  const [clientCpf, setClientCpf] = useState("");
  const [clientCnpj, setClientCnpj] = useState("");
  const [clientIsCompany, setClientIsCompany] = useState(false);
  const [clientEmail, setClientEmail] = useState("");

  const { isUser, isAdmin } = useAuth();

  const [phoneNumber, setPhoneNumber] = useState("");

  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);

  const [address, setAddress] = useState({
    address_city: "",
    address_state: "",
    address_street: "",
    address_neighborhood: "",
    address_number: "",
    address_zipcod: ""
  });

  const [isUpdate, setIsUpdate] = useState(false);
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);

  // refs dos campos obrigatórios (para focar o primeiro que faltar)
  const numberRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const cpfRef = useRef<HTMLInputElement>(null);
  const cnpjRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const cepRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const neighborhoodRef = useRef<HTMLInputElement>(null);
  const streetRef = useRef<HTMLInputElement>(null);

  const containerId = "toaster-client-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  // utils
  const onlyDigits = (s: string) => s?.replace(/\D/g, "") ?? "";
  const isEmpty = (s: string) => s.trim() === "";

  // Estado "ao vivo" dos campos obrigatórios (independe de triedSubmit):
  // verde claro quando preenchido/válido, vermelho quando ainda não.
  const nameOk = clientName.trim().length >= 3;
  const cpfOk = onlyDigits(clientCpf).length === 11;
  const cnpjOk = onlyDigits(clientCnpj).length === 14;
  const phoneDigits = onlyDigits(phoneNumber).length;
  const phoneOk = phoneDigits === 10 || phoneDigits === 11;

  // Endereço é opcional EM GRUPO: se qualquer campo for preenchido, todos
  // passam a ser obrigatórios; se nenhum for tocado, todos ficam opcionais.
  const addressTouched =
    address.address_zipcod.trim() !== "" ||
    address.address_state.trim() !== "" ||
    address.address_city.trim() !== "" ||
    address.address_neighborhood.trim() !== "" ||
    address.address_street.trim() !== "" ||
    address.address_number.trim() !== "";

  const cepOk = onlyDigits(address.address_zipcod).length === 8;
  const stateOk = address.address_state.trim().length >= 2;
  const cityOk = address.address_city.trim() !== "";
  const neighborhoodOk = address.address_neighborhood.trim() !== "";
  const streetOk = address.address_street.trim() !== "";
  const numberOk = address.address_number.trim() !== "";

  // Cores de borda/label para campo obrigatório (verde claro = ok, vermelho = pendente)
  const OK_COLOR = "#66bb6a";
  const ERR_COLOR = "#e57373";
  const requiredSx = (ok: boolean) => {
    const color = ok ? OK_COLOR : ERR_COLOR;
    return {
      "& .MuiOutlinedInput-notchedOutline": { borderColor: color },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: color },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: color,
        borderWidth: 2
      },
      "& .MuiInputLabel-root": { color },
      "& .MuiInputLabel-root.Mui-focused": { color }
    };
  };

  // Borda dos campos de endereço: só sinaliza quando o grupo foi tocado.
  const addressSx = (ok: boolean) =>
    addressTouched ? requiredSx(ok) : undefined;

  const handleCepBlur = useCallback(async () => {
    const cep = onlyDigits(address.address_zipcod);
    if (cep.length !== 8) return;

    setIsFetchingAddress(true);
    try {
      const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
      // brasilapi retorna 404 (com outro corpo) quando o CEP não existe
      if (!res.ok) throw new Error("CEP não encontrado");
      const data = await res.json();

      // preenche apenas o que veio, sem apagar o que o usuário já tinha
      setAddress((prev) => ({
        ...prev,
        address_street: data.street || prev.address_street,
        address_neighborhood: data.neighborhood || prev.address_neighborhood,
        address_city: data.city || prev.address_city,
        address_state: data.state || prev.address_state
      }));
      setTimeout(() => numberRef.current?.focus(), 100);
    } catch {
      autoError("Não foi possível buscar o CEP.");
    } finally {
      setIsFetchingAddress(false);
    }
  }, [address.address_zipcod]);

  const clear = () => {
    setClientName("");
    setClientCpf("");
    setClientEmail("");
    setClientCnpj("");
    setClientIsCompany(false);
    setPhoneNumber("");
    setAddress({
      address_city: "",
      address_state: "",
      address_street: "",
      address_neighborhood: "",
      address_number: "",
      address_zipcod: ""
    });
    setIsUpdate(false);
    setSelectedCompany(null);
  };

  useEffect(() => {
    if (client) {
      setClientName(client.client_name || "");
      setClientCpf(formatCPF(client.client_cpf) || "");
      setClientCnpj(formatCNPJ(client.client_cnpj) || "");
      setClientEmail(client.client_email || "");
      setClientIsCompany(client.client_is_company || false);

      if (client.phone)
        setPhoneNumber(formatPhone(client.phone.phone_number) || "");

      if (client.address) {
        setAddress({
          address_city: client.address.address_city || "",
          address_state: client.address.address_state || "",
          address_street: client.address.address_street || "",
          address_neighborhood: client.address.address_neighborhood || "",
          address_number: client.address.address_number || "",
          address_zipcod: client.address.address_zipcod || ""
        });
      }

      setIsUpdate(true);
    } else {
      clear();
    }
  }, [client]);

  const handleClose = () => {
    clear();
    onClose();
  };

  const handleToggleIsCompany = () => {
    setClientIsCompany((prev) => !prev);
    // limpa o documento que não se aplica
    if (!clientIsCompany) {
      setClientCpf("");
    } else {
      setClientCnpj("");
    }
  };

  const handleSubmit = async () => {
    // Campos obrigatórios em ordem de aparição. Para no primeiro pendente,
    // mostra um toast e foca o campo.
    const required = [
      { ok: nameOk, ref: nameRef, label: "Nome do cliente" },
      clientIsCompany
        ? { ok: cnpjOk, ref: cnpjRef, label: "CNPJ" }
        : { ok: cpfOk, ref: cpfRef, label: "CPF" },
      { ok: phoneOk, ref: phoneRef, label: "Telefone" },
      // Endereço só é exigido se o grupo foi tocado (opcional em conjunto)
      ...(addressTouched
        ? [
            { ok: cepOk, ref: cepRef, label: "CEP" },
            { ok: stateOk, ref: stateRef, label: "Estado" },
            { ok: cityOk, ref: cityRef, label: "Cidade" },
            { ok: neighborhoodOk, ref: neighborhoodRef, label: "Bairro" },
            { ok: streetOk, ref: streetRef, label: "Rua" },
            { ok: numberOk, ref: numberRef, label: "Número" }
          ]
        : [])
    ];

    const missing = required.find((f) => !f.ok);
    if (missing) {
      autoError(`Preencha o campo obrigatório: ${missing.label}.`);
      setTimeout(() => missing.ref.current?.focus(), 0);
      return;
    }

    const normalizedAddress = {
      address_city: address.address_city.trim(),
      address_state: address.address_state.trim(),
      address_street: address.address_street.trim(),
      address_neighborhood: address.address_neighborhood.trim(),
      address_number: address.address_number.trim(),
      address_zipcod: address.address_zipcod.trim()
    };

    const isAddressEmpty = Object.values(normalizedAddress).every((v) =>
      isEmpty(String(v ?? ""))
    );

    const payload = {
      client_name: clientName.trim(),
      client_cpf: clientIsCompany ? null : onlyDigits(clientCpf),
      client_cnpj: clientIsCompany ? onlyDigits(clientCnpj) : null,
      client_is_company: clientIsCompany,
      client_email: clientEmail.trim(),
      phone: { phone_number: onlyDigits(phoneNumber) },
      ...(isAddressEmpty ? {} : { address: normalizedAddress }),
      company_id: selectedCompany?.company_id || null
    };

    try {
      if (isUpdate && client) {
        await api.put(`/client/${client.client_id}`, payload);
        autoSuccess("Cliente atualizado com sucesso!");
      } else {
        await api.post("/client", payload);
        autoSuccess("Cliente cadastrado com sucesso!");
      }

      clear();
      onClose();
    } catch (err: any) {
      console.error("Erro ao salvar:", err.response?.data?.message || err);
      autoError(`Erro: ${err.response?.data?.message || "Erro desconhecido"}`);
    }
  };

  useEffect(() => {
    const url = isAdmin ? "/company/admin" : "/company";

    setLoadingCompanies(true);
    api
      .get(url)
      .then((r) => setCompanies(r.data.items || r.data))
      .catch(console.error)
      .finally(() => setLoadingCompanies(false));
  }, [isAdmin]);

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="create-client-modal"
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border-strong)",
            color: "var(--text)",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            width: "80vw",
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <h5
            style={{
              color: "var(--accent-rose)",
              position: "absolute",
              top: "10px",
              right: "10px"
            }}
          >
            Campos com * são obrigatórios
          </h5>
          <h2 style={{ marginLeft: "15px" }}>
            {isUpdate ? "Editar Cliente" : "Cadastrar Cliente"}
          </h2>

          <InputSection>
            <InputText
              fullWidth
              label="Nome do Cliente"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              required
              margin="normal"
              inputRef={nameRef}
              sx={requiredSx(nameOk)}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "auto",
                textWrap: "nowrap",
                ml: "15px",
                mr: "15px"
              }}
            >
              <Typography variant="body2" fontWeight="bold" mb={0.5}>
                É empresa?
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={clientIsCompany}
                    onChange={handleToggleIsCompany}
                    size="small"
                  />
                }
                label=""
              />
            </Box>

            {!clientIsCompany && (
              <CpfInput
                value={clientCpf}
                onChange={setClientCpf}
                label="CPF"
                sx={{ mb: 2, ...requiredSx(cpfOk) }}
                margin="normal"
                required
                inputRef={cpfRef}
              />
            )}

            {clientIsCompany && (
              <CnpjInput
                value={clientCnpj}
                onChange={setClientCnpj}
                label="CNPJ"
                sx={{ mb: 2, ...requiredSx(cnpjOk) }}
                margin="normal"
                required
                inputRef={cnpjRef}
              />
            )}

            {isAdmin && (
              <Autocomplete
                options={companies}
                sx={{ margin: "normal", width: 600 }}
                getOptionLabel={(opt) => opt.company_name || ""}
                value={selectedCompany}
                isOptionEqualToValue={(option, value) =>
                  option?.company_id === value?.company_id
                }
                onChange={(_, v) => setSelectedCompany(v)}
                renderInput={(params) => (
                  <InputText
                    {...params}
                    label="Empresa"

                    // não requerido; se quiser tornar obrigatório, derive um invalidCompany e passe aqui
                  />
                )}
              />
            )}
          </InputSection>

          <InputSection>
            <PhoneInput
              value={phoneNumber}
              onChange={(value: string) => setPhoneNumber(value)}
              label="Telefone"
              sx={{ width: "35%", ...requiredSx(phoneOk) }}
              margin="normal"
              required
              inputRef={phoneRef}
            />

            <InputText
              sx={{ width: "35%" }}
              fullWidth
              label="E-mail"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              margin="normal"
              // não obrigatório; se quiser obrigar, derive invalidEmail e passe aqui
            />
          </InputSection>

          <h2 style={{ marginLeft: "15px" }}>Endereço</h2>

          <InputSection>
            <InputText
              fullWidth
              label="CEP"
              value={address.address_zipcod}
              onChange={(e) =>
                setAddress({ ...address, address_zipcod: e.target.value })
              }
              onBlur={handleCepBlur}
              margin="normal"
              required={addressTouched}
              inputRef={cepRef}
              inputProps={{ maxLength: 9 }}
              sx={addressSx(cepOk)}
            />

            <CustomTooltip title="Buscar CEP">
              <button
                style={{
                  width: "150px",
                  height: "55px",
                  backgroundColor: "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  marginTop: "6px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)"
                }}
                onClick={handleCepBlur}
                disabled={isFetchingAddress}
                title={isFetchingAddress ? "Buscando..." : undefined}
              >
                <FaSearch />
              </button>
            </CustomTooltip>

            <InputText
              fullWidth
              label="Estado"
              value={address.address_state}
              onChange={(e) =>
                setAddress({ ...address, address_state: e.target.value })
              }
              margin="normal"
              required={addressTouched}
              inputRef={stateRef}
              sx={addressSx(stateOk)}
            />
            <InputText
              fullWidth
              label="Cidade"
              value={address.address_city}
              onChange={(e) =>
                setAddress({ ...address, address_city: e.target.value })
              }
              margin="normal"
              required={addressTouched}
              inputRef={cityRef}
              sx={addressSx(cityOk)}
            />
          </InputSection>

          <InputSection>
            <InputText
              fullWidth
              label="Bairro"
              value={address.address_neighborhood}
              onChange={(e) =>
                setAddress({ ...address, address_neighborhood: e.target.value })
              }
              margin="normal"
              required={addressTouched}
              inputRef={neighborhoodRef}
              sx={addressSx(neighborhoodOk)}
            />
            <InputText
              fullWidth
              label="Rua"
              value={address.address_street}
              onChange={(e) =>
                setAddress({ ...address, address_street: e.target.value })
              }
              margin="normal"
              required={addressTouched}
              inputRef={streetRef}
              sx={addressSx(streetOk)}
            />
            <InputText
              fullWidth
              label="Número"
              value={address.address_number}
              onChange={(e) =>
                setAddress({ ...address, address_number: e.target.value })
              }
              margin="normal"
              required={addressTouched}
              inputRef={numberRef}
              sx={addressSx(numberOk)}
            />
          </InputSection>

          <div
            style={{
              marginTop: "20px",
              textAlign: "right"
            }}
          >
            <Button
              onClick={handleClose}
              style={{ marginRight: "10px" }}
              color="secondary"
            >
              Cancelar
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {isUpdate ? "Atualizar" : "Cadastrar"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ClientCreateModal;
